import { Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { CreditProgressBarWithTooltip } from "@src/appV2/redesign/EmergencyCredits/components/CreditProgressBarWithTooltip";
import {
  FILL_BAR_ANIMATION_DURATION_MS,
  FILL_ICON_ANIMATION_DURATION_MS,
} from "@src/appV2/redesign/EmergencyCredits/hooks/useProgressBarAnimation";
import {
  type CreditProgressBarLabelStatus,
  getCreditProgressBarLabel,
} from "@src/appV2/redesign/EmergencyCredits/utils/getCreditProgressBarLabel";
import { useEffect, useState } from "react";

import { Button } from "../../../../../components/Button";
import { DialogFooter } from "../../../../../components/DialogFooter";
import { PageWrapper } from "../../../../../components/PageWrapper";
import { TextButton } from "../../../../../components/TextButton";
import {
  GOLDEN_RATIO_BOTTOM_FLEX,
  GOLDEN_RATIO_TOP_FLEX,
} from "../../../../../utils/goldenRatioLayout";
import { type CreditStats, type YourEmergencyCreditsPageProps } from "./types";

export const ANIMATION_COMPLETE_DELAY_MS =
  FILL_BAR_ANIMATION_DURATION_MS + FILL_ICON_ANIMATION_DURATION_MS;

function getCreditDisplayCount(
  stats: CreditStats,
  hasCreditAvailable: boolean,
  gainedCredit: boolean
): number {
  if (gainedCredit) {
    return stats.requiredShiftsForNextCredit;
  }

  if (hasCreditAvailable && stats.completedShiftsTowardsNextCredit === 0) {
    return stats.requiredShiftsForNextCredit;
  }

  return stats.completedShiftsTowardsNextCredit;
}

function shouldShowTooltip(
  completedShifts: number,
  requiredShifts: number,
  hasCredit: boolean
): boolean {
  // Show tooltip if: less than full progress AND doesn't have a credit already
  const hasPartialProgress = completedShifts < requiredShifts;
  return hasPartialProgress && !hasCredit;
}

export function YourEmergencyCreditsPage(props: YourEmergencyCreditsPageProps) {
  const {
    backgroundGradient,
    cancellationStats,
    latenessStats,
    gainedCancellationCredit,
    gainedLatenessCredit,
    usedLatenessCredit,
    onContinue,
    onLearnMoreClick,
  } = props;

  const gainedAnyCredit = gainedCancellationCredit || gainedLatenessCredit;

  const [isAnimationComplete, setIsAnimationComplete] = useState(!gainedAnyCredit);

  useEffect(() => {
    if (!gainedAnyCredit) {
      setIsAnimationComplete(true);
      return;
    }

    setIsAnimationComplete(false);
    const completeTimer = setTimeout(() => {
      setIsAnimationComplete(true);
    }, ANIMATION_COMPLETE_DELAY_MS);

    return () => {
      clearTimeout(completeTimer);
    };
  }, [gainedAnyCredit]);

  const hasCancellationCredit = cancellationStats.totalAvailableCredits > 0;
  const hasLatenessCredit = latenessStats.totalAvailableCredits > 0 || usedLatenessCredit;

  function getCreditLabelStatus({
    hasExistingCredit,
    gainedCredit,
  }: {
    hasExistingCredit: boolean;
    gainedCredit: boolean;
  }): CreditProgressBarLabelStatus {
    if (gainedCredit) {
      return isAnimationComplete ? "earned" : "unavailable";
    }

    return hasExistingCredit ? "available" : "unavailable";
  }

  const cancellationCurrentCount = getCreditDisplayCount(
    cancellationStats,
    hasCancellationCredit,
    gainedCancellationCredit
  );

  const latenessCurrentCount = getCreditDisplayCount(
    latenessStats,
    hasLatenessCredit,
    gainedLatenessCredit
  );

  return (
    <PageWrapper
      sx={(theme) => ({
        backgroundColor: theme.palette.background.tertiary,
        background: backgroundGradient ?? theme.palette.background.tertiary,
        display: "flex",
        flexDirection: "column",
      })}
    >
      <Stack sx={{ flex: 1 }}>
        <Box sx={{ flex: GOLDEN_RATIO_TOP_FLEX, minHeight: 0 }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingX: 4,
            paddingTop: 2,
          }}
        >
          {/* Header Section */}
          <Stack spacing={4} alignItems="center" textAlign="center" sx={{ width: "100%" }}>
            <Text variant="h3">
              {gainedAnyCredit ? "You've earned an emergency credit!" : "Your Emergency Credits"}
            </Text>

            {!gainedAnyCredit && (
              <Text
                variant="h6"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                })}
              >
                Working shifts earns progress towards credits that protect your Clipboard Score.
              </Text>
            )}
          </Stack>

          {/* Progress Card Section */}
          <Box
            sx={(theme) => ({
              marginTop: 12,
              backgroundColor: theme.palette.background.primary,
              borderRadius: "15px",
              paddingX: 6,
              paddingTop: 6,
              paddingBottom: 8,
              width: "100%",
            })}
          >
            <Stack spacing={5}>
              {/* Cancellation Credit Progress */}
              <CreditProgressBarWithTooltip
                completedCount={cancellationCurrentCount}
                requiredCount={cancellationStats.requiredShiftsForNextCredit}
                label={getCreditProgressBarLabel(
                  "cancellation",
                  getCreditLabelStatus({
                    hasExistingCredit: hasCancellationCredit,
                    gainedCredit: gainedCancellationCredit,
                  })
                )}
                color="blue"
                mode={hasCancellationCredit && !gainedCancellationCredit ? "static" : "fill"}
                showTooltip={
                  !gainedCancellationCredit &&
                  shouldShowTooltip(
                    cancellationStats.completedShiftsTowardsNextCredit,
                    cancellationStats.requiredShiftsForNextCredit,
                    hasCancellationCredit
                  )
                }
                popIconOnFull={gainedCancellationCredit}
              />

              {/* Lateness Credit Progress */}
              <CreditProgressBarWithTooltip
                completedCount={latenessCurrentCount}
                requiredCount={latenessStats.requiredShiftsForNextCredit}
                label={getCreditProgressBarLabel(
                  "lateness",
                  getCreditLabelStatus({
                    hasExistingCredit: hasLatenessCredit,
                    gainedCredit: gainedLatenessCredit,
                  })
                )}
                color="green"
                mode={hasLatenessCredit && !gainedLatenessCredit ? "static" : "fill"}
                showTooltip={
                  !gainedLatenessCredit &&
                  shouldShowTooltip(
                    latenessStats.completedShiftsTowardsNextCredit,
                    latenessStats.requiredShiftsForNextCredit,
                    hasLatenessCredit
                  )
                }
                popIconOnFull={gainedLatenessCredit}
              />
            </Stack>
          </Box>
        </Box>

        <Box sx={{ flex: GOLDEN_RATIO_BOTTOM_FLEX, minHeight: 0 }} />
      </Stack>

      <DialogFooter orientation="horizontal">
        <Stack spacing={5} alignItems="center" sx={{ width: "100%" }}>
          <TextButton onClick={onLearnMoreClick}>What are Emergency Credits?</TextButton>

          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!isAnimationComplete}
            onClick={onContinue}
          >
            Continue
          </Button>
        </Stack>
      </DialogFooter>
    </PageWrapper>
  );
}
